import React, { useMemo } from 'react'
import {
  FaBuilding,
  FaCalendarAlt,
  FaCheck,
  FaCreditCard,
  FaDollarSign,
  FaExternalLinkAlt,
  FaHashtag,
  FaTag,
  FaUniversity,
} from 'react-icons/fa'
import { KTSVG } from '../../../_metronic/helpers'
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp'
import DateComp from '../../../components/dateComponent/DateComp'
import { constraintConfig } from '../../../constraintConfig'
import { buildFinalMergePreview, MergeV2ModeResolved } from './mergeFinalPreview'
import {
  formatReceiptNumberDisplay,
  getMergeAdminMeta,
  getMergeAttachmentUrl,
  getReceiptNumber,
} from './MergeSelectedLineItemHeader'

type Props = {
  fromItem: any
  intoItem: any
  mergeMode: MergeV2ModeResolved
  matchHints?: {
    dateMatch?: boolean
    amountMatch?: boolean
    isBestMatch?: boolean
  }
}

const parseAmount = (value: any): number => {
  const n = Number(String(value ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

const sameItemDate = (a: any, b: any): boolean => {
  if (!a || !b) return false
  return String(a).split('T')[0] === String(b).split('T')[0]
}

const sourceLabel = (recordSourceId?: number) => {
  if (recordSourceId === constraintConfig.recordSourceId.BankFeed) return 'Bank'
  if (recordSourceId === constraintConfig.recordSourceId.WebApp) return 'Web'
  if (recordSourceId === constraintConfig.recordSourceId.Mobile) return 'Mobile'
  if (recordSourceId === constraintConfig.recordSourceId.GorillaExpense) return 'Gorilla'
  return '—'
}

const sourceIcon = (recordSourceId?: number) => {
  if (recordSourceId === constraintConfig.recordSourceId.BankFeed) {
    return <FaUniversity className="text-primary" size={12} />
  }
  if (recordSourceId === constraintConfig.recordSourceId.WebApp) {
    return <KTSVG path="/media/icons/figmaIcons/Web.svg" className="svg-icon-5" />
  }
  if (recordSourceId === constraintConfig.recordSourceId.Mobile) {
    return <KTSVG path="/media/icons/figmaIcons/Mobile.svg" className="svg-icon-5" />
  }
  return <FaBuilding className="text-muted" size={12} />
}

const MatchBadge = ({
  children,
  strong,
}: {
  children: React.ReactNode
  strong?: boolean
}) => (
  <span className={`badge ${strong ? 'badge-success' : 'badge-light-success'}`}>
    {children}
  </span>
)

const PreviewCol = ({
  icon,
  label,
  children,
  badge,
  last,
}: {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
  badge?: React.ReactNode
  last?: boolean
}) => (
  <div
    className={`merge-result-preview-col d-flex align-items-start gap-2 px-2 py-1${last ? ' merge-result-preview-col--last' : ''}`}
    style={{
      minWidth: 0,
      flex: '1 1 0',
    }}
  >
    <div className="text-muted mt-1 flex-shrink-0">{icon}</div>
    <div className="min-w-0">
      <div className="text-muted text-uppercase fw-semibold" style={{ lineHeight: 1.15 }}>
        {label}
      </div>
      <div
        className="fw-semibold merge-result-preview-value d-inline-flex flex-wrap align-items-center gap-1"
        style={{ lineHeight: 1.25 }}
      >
        <span className="text-truncate">{children}</span>
        {badge}
      </div>
    </div>
  </div>
)

/** Single-row merged result preview matching product mockup. */
const MergeResultPreviewRow = ({ fromItem, intoItem, mergeMode, matchHints }: Props) => {
  const preview = buildFinalMergePreview(fromItem, intoItem, mergeMode)
  const receiptNo = preview.receiptNo !== '—' ? preview.receiptNo : getReceiptNumber(intoItem)
  const receiptDisplay = formatReceiptNumberDisplay(receiptNo)
  const hasReceipt = preview.attachmentLabel === 'Has receipt'
  const trackingId = getMergeAdminMeta(intoItem).trackingId

  /** Final receipt URL after merge rules (Bank always takes From attachment; SS→SS only if Into empty). */
  const finalAttachmentUrl = useMemo(() => {
    const fromAtt = getMergeAttachmentUrl(fromItem)
    const intoAtt = getMergeAttachmentUrl(intoItem)
    if (mergeMode === 'bank') {
      return fromAtt || intoAtt || ''
    }
    return intoAtt || fromAtt || ''
  }, [fromItem, intoItem, mergeMode])

  const openFinalReceipt = () => {
    if (!finalAttachmentUrl) return
    window.open(finalAttachmentUrl, '_blank', 'noopener,noreferrer')
  }

  const matches = useMemo(() => {
    const dateMatch =
      matchHints?.dateMatch ?? sameItemDate(fromItem?.itemDate, intoItem?.itemDate)
    const amountMatch =
      matchHints?.amountMatch ??
      (Math.abs(parseAmount(fromItem?.amount) - parseAmount(intoItem?.amount)) < 0.001 &&
        parseAmount(intoItem?.amount) > 0)
    const isBestMatch = matchHints?.isBestMatch ?? (dateMatch && amountMatch)
    const vendorMatch =
      !!fromItem?.vendor?.name &&
      String(fromItem.vendor.name).trim().toLowerCase() ===
        String(intoItem?.vendor?.name || '')
          .trim()
          .toLowerCase()
    const categoryMatch =
      !!fromItem?.expenseCategory?.title &&
      String(fromItem.expenseCategory.title).trim().toLowerCase() ===
        String(intoItem?.expenseCategory?.title || '')
          .trim()
          .toLowerCase()
    return { dateMatch, amountMatch, isBestMatch, vendorMatch, categoryMatch }
  }, [fromItem, intoItem, matchHints])

  return (
    <div
      className="merge-result-preview border rounded mt-1 overflow-hidden"
    >
      <div
        className="merge-result-preview-header d-flex flex-wrap align-items-center justify-content-between gap-2 px-2 px-lg-3 py-2"
      >
        <div className="d-flex align-items-center gap-2">
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success"
            style={{ width: 22, height: 22 }}
          >
            <FaCheck className="text-white" size={10} />
          </span>
          <div>
            <div className="fw-bold text-gray-900">
              Merged Result (Preview)
            </div>
            <div className="text-muted">
              This is how the merged line item will look after merge.
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap align-items-center justify-content-center gap-1 flex-grow-1 px-2">
          {matches.isBestMatch ? <MatchBadge strong>Best match</MatchBadge> : null}
          {matches.dateMatch ? <MatchBadge>Date match</MatchBadge> : null}
          {matches.amountMatch ? <MatchBadge>Amount match</MatchBadge> : null}
          {matches.vendorMatch ? <MatchBadge>Vendor match</MatchBadge> : null}
          {matches.categoryMatch ? <MatchBadge>Category match</MatchBadge> : null}
          {!matches.dateMatch &&
          !matches.amountMatch &&
          !matches.vendorMatch &&
          !matches.categoryMatch ? (
            <span className="text-muted">
              No field matches
            </span>
          ) : null}
        </div>

        <div className="d-flex align-items-center gap-2">
          <span className="text-primary fw-semibold merge-result-preview-value" title={receiptNo}>
            Receipt # {receiptDisplay}
          </span>
          <span
            className={`badge ${hasReceipt ? 'badge-light-success' : 'badge-light-warning'}`}
          >
            {hasReceipt ? 'Has receipt' : 'No receipt'}
          </span>
          {hasReceipt && finalAttachmentUrl ? (
            <button
              type="button"
              className="btn btn-sm btn-light-primary d-inline-flex align-items-center gap-1 py-1 px-2"
              title="Open final attached receipt in new tab"
              onClick={openFinalReceipt}
            >
              <FaExternalLinkAlt size={10} />
              Open in new tab
            </button>
          ) : null}
        </div>
      </div>

      <div
        className="merge-result-preview-body d-flex flex-wrap align-items-stretch border rounded mx-2 mx-lg-3 my-2 py-1"
      >
        <PreviewCol icon={<FaHashtag size={11} />} label="Tracking ID">
          {trackingId}
        </PreviewCol>
        <PreviewCol
          icon={<FaTag size={11} />}
          label="Category"
          badge={matches.categoryMatch ? <MatchBadge>Match</MatchBadge> : null}
        >
          {preview.category}
        </PreviewCol>
        <PreviewCol
          icon={<FaBuilding size={11} />}
          label="Vendor"
          badge={matches.vendorMatch ? <MatchBadge>Match</MatchBadge> : null}
        >
          {preview.vendor}
        </PreviewCol>
        <PreviewCol icon={<FaCreditCard size={11} />} label="Payment Method">
          {preview.paymentMethod}
        </PreviewCol>
                <PreviewCol
          icon={<FaCalendarAlt size={11} />}
          label="Line Item Date"
          badge={
            matches.dateMatch ? (
              <MatchBadge strong={matches.isBestMatch && !matches.amountMatch}>Date match</MatchBadge>
            ) : null
          }
        >
          {intoItem?.itemDate ? <DateComp formattedDate={intoItem.itemDate} /> : preview.itemDate}
        </PreviewCol>
        <PreviewCol
          icon={<FaDollarSign size={11} />}
          label="Amount"
          badge={
            matches.amountMatch ? (
              <MatchBadge strong={matches.isBestMatch}>
                {matches.isBestMatch ? 'Best match' : 'Amount match'}
              </MatchBadge>
            ) : null
          }
        >
          {intoItem?.amount != null ? <CurrencyComp amount={intoItem.amount} /> : `$${preview.amount}`}
        </PreviewCol>

        <PreviewCol icon={sourceIcon(intoItem?.recordSourceId)} label="Item Source" last>
          {sourceLabel(intoItem?.recordSourceId)}
        </PreviewCol>
      </div>
    </div>
  )
}

export default MergeResultPreviewRow
